import * as signalR from "@microsoft/signalr"
import { useEffect, useRef, useState } from "react"
import { DeliveryEntity } from "../../Objects/DeliveryEntity/DeliveryEntity"
import { deliveryMemory } from "../../Objects/Memory/Memory"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useTranslation } from "react-i18next"
import { EDeliveryStatusType } from "../../Enum/EDeliveryStatusType"
import { dedupeDeliveries, filterActiveCourierDeliveries } from "./deliveryListUtils"

interface DeliveryStatusHubEvent {
  deliveryId: number
  status: keyof typeof EDeliveryStatusType
}

const getNormalizedCourierToken = () => localStorage.getItem(API_ROUTES.TOKEN_COURIER)?.replace(/['"]/g, "") ?? ""

export function useDeliveryHub(
  courierId: string | undefined,
  isOnline: boolean,
  onNewDelivery?: (delivery: DeliveryEntity) => void,
) {
  const { t } = useTranslation("general")

  const { setError } = useError()

  const [deliveries, setDeliveries] = useState<DeliveryEntity[]>(deliveryMemory.listData)

  const connectionRef = useRef<signalR.HubConnection | null>(null)
  
  const reconnectTimeoutRef = useRef<number | null>(null)

  const onNewDeliveryRef = useRef(onNewDelivery)

  const hasShownReconnectError = useRef(false)

  const applyDeliveries = (nextList: DeliveryEntity[]) => {
    const now = new Date()

    const normalized = dedupeDeliveries(nextList).filter((item) => filterActiveCourierDeliveries(item, now))

    deliveryMemory.setList(normalized)
    setDeliveries(normalized)
  }

  useEffect(() => {
    onNewDeliveryRef.current = onNewDelivery
  }, [onNewDelivery])

  useEffect(() => {
    if (!courierId || !isOnline) {
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }

      if (connectionRef.current) {
        connectionRef.current.stop()
        connectionRef.current = null
      }

      const resetTimer = window.setTimeout(() => {
        applyDeliveries([])
      }, 0)

      return () => window.clearTimeout(resetTimer)
    }

    let isDisposed = false

    const scheduleReconnect = (delayMs: number) => {
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current)
      }

      reconnectTimeoutRef.current = window.setTimeout(() => {
        reconnectTimeoutRef.current = null

        if (!isDisposed && isOnline && courierId) {
          startConnection()
        }
      }, delayMs)
    }

    const startConnection = async () => {
      try {
        if (connectionRef.current) {
          await connectionRef.current.stop()
        }

        const connection = new signalR.HubConnectionBuilder()
          .withUrl(API_ROUTES.HUB.DELIVERY, {
            accessTokenFactory: getNormalizedCourierToken,
          })
          .withAutomaticReconnect([0, 2000, 5000, 10000, 30000, 60000])
          .build()

        connection.on("delivery:new", (delivery: DeliveryEntity) => {
          const newDelivery = new DeliveryEntity(delivery)

          onNewDeliveryRef.current?.(newDelivery)
          applyDeliveries([newDelivery, ...deliveryMemory.listData])
        })

        connection.on("delivery:status", (event: DeliveryStatusHubEvent) => {
          const mappedStatus = EDeliveryStatusType[event.status]

          if (mappedStatus === undefined) {
            return
          }

          const nextList = deliveryMemory.listData.map((item) => {
            if (item.id !== event.deliveryId) {
              return item
            }

            return new DeliveryEntity({ ...item, status: mappedStatus })
          })

          applyDeliveries(nextList)
        })

        connection.onreconnecting((error) => {
          if (!hasShownReconnectError.current) {
            const message = error instanceof Error ? error.message : t("label_unknown_error_description")

            setError(t("label_title_erro"), message)
            hasShownReconnectError.current = true
          }
        })

        connection.onreconnected(async () => {
          hasShownReconnectError.current = false

          if (courierId) {
            await connection.invoke("JoinCourierGroup", courierId)
          }

          applyDeliveries(deliveryMemory.listData)
        })

        connection.onclose((error) => {
          if (isDisposed) {
            return
          }

          if (error) {
            const message = error instanceof Error ? error.message : t("label_unknown_error_description")

            setError(t("label_title_erro"), message)
          }

          scheduleReconnect(2000)
        })

        await connection.start()

        if (isDisposed) {
          await connection.stop()
          return
        }

        if (courierId) {
          await connection.invoke("JoinCourierGroup", courierId)
        }

        connectionRef.current = connection
      } catch {
        setError(t("label_title_erro"), t("label_connect_error"))
        scheduleReconnect(5000)
      }
    }

    startConnection()

    const reconnectWhenVisible = () => {
      if (document.visibilityState !== "visible") {
        return
      }

      const currentConnection = connectionRef.current

      if (!currentConnection || currentConnection.state === signalR.HubConnectionState.Disconnected) {
        startConnection()
      }
    }

    window.addEventListener("focus", reconnectWhenVisible)
    window.addEventListener("online", reconnectWhenVisible)
    document.addEventListener("visibilitychange", reconnectWhenVisible)

    return () => {
      isDisposed = true

      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }

      window.removeEventListener("focus", reconnectWhenVisible)
      window.removeEventListener("online", reconnectWhenVisible)
      document.removeEventListener("visibilitychange", reconnectWhenVisible)

      connectionRef.current?.stop()
      connectionRef.current = null
    }
  }, [courierId, isOnline])

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (!isOnline) {
        return
      }

      applyDeliveries(deliveryMemory.listData)
    }, 1000)

    return () => window.clearInterval(interval)
  }, [isOnline])

  return deliveries
}
